import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CartRepository } from '@asm/features/sync/repositories/cart.repository';
import { OrderHistoryRepository } from '@asm/features/sync/repositories/order-history.repository';
import { OrderDeadlineRepository } from '@asm/features/sync/repositories/order-deadline.repository';
import { OrderReferenceRepository } from '@asm/features/sync/repositories/order-reference.repository';
import { DocumentTypeRepository } from '@asm/features/sync/repositories/document-type.repository';
import { ProductRepository } from '@asm/features/sync/repositories/product.repository';
import { Cart, CartEntry } from '@asm/features/quick-order/models/cart.model';
import { LocalOrderDeadline, LocalOrderReference } from '@asm/features/quick-order/models/offline-reference-data.model';
import { OfflineHelpersService } from '@asm/features/sync/interceptor/offline-helpers.service';
import { WarehouseOfflineHandler } from '@asm/features/sync/interceptor/handlers/warehouse.handler';
import { DefaultLocalCartService } from '@asm/features/sync/services/cart/default-local-cart.service';
import { OrderReferenceSelectItem } from '@asm/features/quick-order/models/order-reference-select.model';
import { MinimumOrderRuleRepository } from '@asm/features/sync/repositories/minimum-order-rule.repository';
import { StockIssue } from '@asm/features/quick-order-summary/models/stock-issue.model';
import { StockLevelRepository } from '@asm/features/sync/repositories/stock-level.repository';
import { PaymentMethodRepository } from '@asm/features/sync/repositories/payment-method.repository';
import { PaymentConditionRepository } from '@asm/features/sync/repositories/payment-condition.repository';
import { DefaultLocalCouponService } from '@asm/features/sync/services/coupon/default-local-coupon.service';
import { generateUUID } from '@shared/helpers/generate-uuid';
import { OfflineOrderOutboxRepository } from '@asm/features/sync/repositories/offline-order-outbox.repository';
import { OfflineOrderOutboxState } from '@asm/features/sync/services/orders/offline-order-outbox-state.service';
import { OfflineOrderOutboxEntry } from '@asm/features/sync/models/offline-order-sync.model';
import { normalizeBoolean } from '@asm/features/sync/services/sync/sync.normalizers';
import { ASM_CUSTOMER_ID_KEY, ASM_UNIT_ID_KEY } from '@shared/tools/constants';
import { StockAllocationType } from '@asm/features/quick-order/models/stock-allocation.model';

export type PlaceOrderResult = { offlineOrder: true; localId: string } | { orderError: { message: string } };

export interface CopyOrderToCartResult {
  cart: Cart;
  stockIssues: StockIssue[];
}

/**
 * Serviço que centraliza toda a regra de negócio de pedidos offline:
 * place-order, validações de data, horário limite e valor mínimo.
 */
@Injectable({ providedIn: 'root' })
export class DefaultLocalOrderService {
  private readonly helper = inject(OfflineHelpersService);
  private readonly cartRepo = inject(CartRepository);
  private readonly orderHistoryRepo = inject(OrderHistoryRepository);
  private readonly orderDeadlineRepo = inject(OrderDeadlineRepository);
  private readonly minimumOrderRuleRepo = inject(MinimumOrderRuleRepository);
  private readonly orderReferenceRepo = inject(OrderReferenceRepository);
  private readonly warehouseHandler = inject(WarehouseOfflineHandler);
  private readonly cartService = inject(DefaultLocalCartService);
  private readonly documentTypeRepo = inject(DocumentTypeRepository);
  private readonly productRepo = inject(ProductRepository);
  private readonly couponService = inject(DefaultLocalCouponService);
  private readonly stockLevelRepo = inject(StockLevelRepository);
  private readonly paymentMethodRepo = inject(PaymentMethodRepository);
  private readonly paymentConditionRepo = inject(PaymentConditionRepository);
  private readonly outboxRepo = inject(OfflineOrderOutboxRepository);
  private readonly outboxState = inject(OfflineOrderOutboxState);

  /**
   * Executa todo o fluxo de place-order offline:
   * busca o carrinho, valida, persiste o pedido no outbox e limpa o carrinho.
   */
  async placeOrder(cartId: string, b2bUnitCode: string, customerId: string): Promise<PlaceOrderResult> {
    const cart = cartId
      ? await firstValueFrom(this.cartRepo.getByKey(cartId))
      : await firstValueFrom(this.cartRepo.getActive());

    if (!cart) {
      throw new Error('Nenhum carrinho ativo para salvar como pedido offline');
    }

    const availableDays = await this.warehouseHandler.getAvailableWeekDays(
      b2bUnitCode,
      String(cart.warehouseCode ?? '').trim(),
    );

    const deliveryDateError = await this.validateDesiredDeliveryDate(cart, b2bUnitCode, availableDays);
    if (deliveryDateError) return { orderError: { message: deliveryDateError } };

    const deadlineError = await this.validateOrderDeadline(cart, customerId);
    if (deadlineError) return { orderError: { message: deadlineError } };

    const minimumOrderError = await this.validateMinimumOrderValue(cart);
    if (minimumOrderError) return { orderError: { message: minimumOrderError } };

    const clientOrderId = generateUUID();
    const b2bUnit = await this.helper.readStoredB2BUnitDetails();
    const isQuotation = await this.resolveIsQuotation(cart.documentTypeCode);

    // Use localStorage as canonical source for IDs to ensure consistency
    // between what's stored in the outbox and what's read by the list/detail views.
    const unitUid = b2bUnit?.uid || (localStorage.getItem(ASM_UNIT_ID_KEY) ?? '');
    const custUid = String(cart.customer ?? customerId) || (localStorage.getItem(ASM_CUSTOMER_ID_KEY) ?? '');

    // Build display snapshot (with resolved descriptions)
    const cartWithDescriptions = await this.cartService.applyReferenceDescriptions(cart);

    const entry: OfflineOrderOutboxEntry = {
      clientOrderId,
      status: 'PENDING',
      request: {
        clientOrderId,
        b2bUnitUid: unitUid,
        customerUid: custUid,
        isQuotation,
        documentTypeCode: cart.documentTypeCode ?? null,
        warehouseCode: cart.warehouseCode ?? null,
        desiredDeliveryDate: cart.desiredDeliveryDate ? new Date(cart.desiredDeliveryDate).toISOString() : null,
        paymentMethodCode: cart.paymentMethodCode ?? null,
        paymentConditionCode: cart.paymentConditionCode ?? null,
        freightTypeCode: cart.freightTypeCode ?? null,
        purchaseOrder: cart.purchaseOrder ?? null,
        observation: cart.observation ?? null,
        coupon: cart.couponCode ?? null,
        bankCode: (cart as any).bankCode ?? null,
        referenceOrderCode: cart.referenceOrderCode ?? null,
        entries: (cart.entries ?? []).map(e => ({
          productCode: e.productCode ?? '',
          qty: e.qty ?? 0,
          stockAllocations: e.stockAllocations ?? [],
          warehouseCode: e.warehouseCode ?? null,
          observation: e.observation ?? null,
        })),
      },
      display: {
        schemaVersion: 1,
        documentTypeDescription: cartWithDescriptions.documentTypeDescription ?? null,
        freightTypeDescription: cartWithDescriptions.freightTypeDescription ?? null,
        paymentMethodDescription: cartWithDescriptions.paymentMethodDescription ?? null,
        paymentConditionDescription: cartWithDescriptions.paymentConditionDescription ?? null,
        warehouseCode: cart.warehouseCode ?? null,
        totalPrice: Number(cart.totalPrice ?? 0),
        totalTax: Number(cart.totalTax ?? 0),
        totalDiscounts: Number(cart.totalDiscounts ?? 0),
        ipi: Number(cart.totalIpi ?? 0),
        st: Number(cart.totalSt ?? 0),
        icms: Number(cart.totalICMS ?? 0),
        entries: (cartWithDescriptions.entries ?? []).map(e => ({
          productCode: e.productCode ?? '',
          productName: e.productName ?? '',
          productEan: e.productEan ?? null,
          qty: e.qty ?? 0,
          basePrice: e.basePrice?.value ?? 0,
          totalPrice: e.totalPrice?.value ?? 0,
          ipi: e.ipi?.value ?? 0,
          st: e.st?.value ?? 0,
          icms: e.icms?.value ?? 0,
          warehouseCode: e.warehouseCode ?? null,
          observation: e.observation ?? null,
          imageUrls: (e.imageUrls?.filter(Boolean) as string[]) ?? [],
          stockAllocations: e.stockAllocations ?? [],
        })),
      },
      receipt: null,
      remoteObserved: false,
      attempts: 0,
      lastAttemptAt: null,
      lastError: null,
      createdAt: new Date().toISOString(),
      b2bUnitUid: unitUid,
      customerUid: custUid,
    };

    await firstValueFrom(this.outboxRepo.addOrUpdate(entry));
    await firstValueFrom(this.cartRepo.clear());
    await this.outboxState.refresh();

    return { offlineOrder: true, localId: clientOrderId };
  }

  /**
   * Resolve se um tipo de documento é orçamento (isQuotation).
   * Consulta o DocumentTypeRepository para ler o campo isQuotation do documento.
   */
  private async resolveIsQuotation(documentTypeCode: string | null | undefined): Promise<boolean> {
    if (!documentTypeCode) return false;
    try {
      const allDts = await firstValueFrom(this.documentTypeRepo.getAll());
      const dt = allDts.find(d => d.code === documentTypeCode);
      return normalizeBoolean((dt as any)?.isQuotation);
    } catch {
      return false;
    }
  }

  /**
   * Valida se a data de entrega desejada está disponível para a rota do armazém.
   */
  async validateDesiredDeliveryDate(cart: Cart, b2bUnitCode: string, availableDays: number[]): Promise<string | null> {
    const desiredDate = this.helper.parseDateOnly(cart.desiredDeliveryDate);
    const warehouseCode = String(cart.warehouseCode ?? '').trim();

    if (!desiredDate || !warehouseCode || !b2bUnitCode) return null;
    if (availableDays.length === 0) return null;

    if (!availableDays.includes(desiredDate.getDay())) {
      return 'Não é possível concluir o pedido, pois a data de entrega desejada não está disponível para a rota de entrega selecionada.';
    }

    return null;
  }

  /**
   * Valida se o horário limite de fechamento do pedido foi ultrapassado.
   */
  async validateOrderDeadline(cart: Cart, customerId: string): Promise<string | null> {
    const warehouseCode = String(cart.warehouseCode ?? '').trim();
    if (!warehouseCode) return null;

    const rules = await firstValueFrom(this.orderDeadlineRepo.getAll());
    const rule = this.findOrderDeadlineRule(rules, warehouseCode, customerId);
    if (!rule?.deadline) return null;

    const [limitHour, limitMinute] = rule.deadline.split(':').map(value => Number(value));
    if (Number.isNaN(limitHour) || Number.isNaN(limitMinute)) return null;

    const now = new Date();
    const limitDate = new Date();
    limitDate.setHours(limitHour, limitMinute, 0, 0);

    if (now > limitDate) {
      return 'Não é possível concluir o pedido, pois o horário limite do centro fornecedor foi ultrapassado.';
    }

    return null;
  }

  /**
   * Valida se o pedido atende ao valor mínimo e peso mínimo configurados.
   */
  async validateMinimumOrderValue(cart: Cart): Promise<string | null> {
    const b2bUnit = await this.helper.readStoredB2BUnitDetails();
    const warehouseCode = String(cart.warehouseCode ?? '').trim();

    if (!b2bUnit?.segmentation || !b2bUnit.distributionChannel || !warehouseCode) return null;

    const rules = await firstValueFrom(this.minimumOrderRuleRepo.getAll());
    const rule = rules.find(
      item =>
        item.shouldBlockCheckout &&
        String(item.distributionChannelCode ?? '').trim() === b2bUnit.distributionChannel &&
        String(item.segmentationCode ?? '').trim() === b2bUnit.segmentation &&
        String(item.warehouseCode ?? '').trim() === warehouseCode,
    );

    if (!rule) return null;

    const referenceOrderCode = String(cart.referenceOrderCode ?? '').trim();
    const referenceOrder: LocalOrderReference | null = referenceOrderCode
      ? ((await firstValueFrom(this.orderReferenceRepo.getByKey(referenceOrderCode))) ?? null)
      : null;

    const cartTotal = Number(cart.totalPrice ?? 0);
    const referenceTotal = this.helper.parseNumericValue(referenceOrder?.totalPrice);
    const minimumOrderValue = this.helper.parseNumericValue(rule.minimumOrderValue);

    if (minimumOrderValue > 0) {
      const effectiveTotalPrice = cartTotal + referenceTotal;
      if (effectiveTotalPrice < minimumOrderValue) {
        return `Não é possível concluir o pedido. O valor mínimo permitido é de ${this.helper.formatCurrency(minimumOrderValue)}. Ajuste o pedido para prosseguir.`;
      }
    }

    const minimumWeight = this.helper.parseNumericValue(rule.minimumWeight);
    if (minimumWeight > 0) {
      const cartWeight = Number(cart.totalWeight ?? 0);
      const referenceWeight = this.helper.parseNumericValue(referenceOrder?.totalWeight);
      const totalWeight = cartWeight + referenceWeight;

      if (totalWeight < minimumWeight) {
        return `Não é possível concluir o pedido. O peso mínimo permitido é de ${minimumWeight} kg. Ajuste o pedido para prosseguir.`;
      }
    }

    return null;
  }

  /** Resolves the document type description from the store when the cart doesn't have it. */
  private async resolveDocumentTypeDescription(code: string | null | undefined): Promise<string | null> {
    if (!code) return null;
    try {
      const allDts = await firstValueFrom(this.documentTypeRepo.getAll());
      return allDts.find(d => d.code === code)?.description ?? null;
    } catch {
      return null;
    }
  }

  findOrderDeadlineRule(
    rules: LocalOrderDeadline[],
    warehouseCode: string,
    customerId: string,
  ): LocalOrderDeadline | undefined {
    const normalizedWarehouse = warehouseCode.trim();
    const normalizedCustomer = customerId.trim();

    if (normalizedCustomer) {
      const specific = rules.find(
        rule =>
          String(rule.warehouseCode ?? '').trim() === normalizedWarehouse &&
          [rule.employeeUid, rule.employeeId].some(value => String(value ?? '').trim() === normalizedCustomer),
      );
      if (specific) return specific;
    }

    return rules.find(
      rule =>
        String(rule.warehouseCode ?? '').trim() === normalizedWarehouse &&
        !String(rule.employeeUid ?? '').trim() &&
        !String(rule.employeeId ?? '').trim(),
    );
  }

  /**
   * Converte um pedido do histórico offline em um novo carrinho ativo.
   * Espelho offline do endpoint POST /order/:uid/:uid/:orderCode/copy-to-cart.
   *
   * Suporta dois tipos de fonte:
   * 1. OrderHistoryRepository — pedidos sincronizados do servidor.
   * 2. OfflineOrderOutboxRepository — pedidos criados offline (order_outbox),
   *    identificados pelo clientOrderId usado como orderCode na tela de detalhe.
   */
  async copyOrderToCart(b2bUnitUid: string, customerUid: string, orderCode: string): Promise<CopyOrderToCartResult> {
    const order = await firstValueFrom(this.orderHistoryRepo.getByOrderCode(b2bUnitUid, customerUid, orderCode));

    if (!order) {
      // Fallback: tenta encontrar no outbox pelo clientOrderId (usado como orderCode para pedidos locais)
      const outboxEntry = await firstValueFrom(this.outboxRepo.getByKey(orderCode));
      if (outboxEntry) {
        return this.copyFromOutboxEntry(outboxEntry, b2bUnitUid);
      }
      throw new Error(`[copyOrderToCart] Pedido não encontrado: ${orderCode}`);
    }

    const cart = this.cartService.createEmptyCart();

    cart.warehouseCode = order.entries?.[0]?.warehouseCode ?? undefined;
    cart.documentTypeCode = order.documentTypeCode ?? undefined;
    cart.freightTypeCode = order.freightTypeCode ?? undefined;
    cart.purchaseOrder = order.purchaseOrder ?? undefined;
    cart.observation = order.observation ?? undefined;
    cart.desiredDeliveryDate = order.desiredDeliveryDate ?? undefined;

    cart.paymentMethodCode = await this.resolvePaymentCode(
      order.paymentMethodCode,
      order.paymentMethodDescription,
      this.paymentMethodRepo,
    );
    cart.paymentMethodDescription = order.paymentMethodDescription ?? undefined;

    cart.paymentConditionCode = await this.resolvePaymentCode(
      order.paymentConditionCode,
      order.paymentConditionDescription,
      this.paymentConditionRepo,
    );
    cart.paymentConditionDescription = order.paymentConditionDescription ?? undefined;

    const orderEntries = order.entries ?? [];
    const allStockLevels = await firstValueFrom(this.stockLevelRepo.getAll());
    const stockIssues: StockIssue[] = [];
    const cartEntries: CartEntry[] = [];
    let entryIndex = 0;

    for (const e of orderEntries) {
      const product = await firstValueFrom(this.productRepo.getByKey(e.productCode));

      const stockLevel = allStockLevels.find(
        s => s.productCode === e.productCode && s.warehouseCode === (e.warehouseCode ?? ''),
      );
      const available =
        stockLevel != null ? Math.max(0, (stockLevel.available ?? 0) - (stockLevel.reserved ?? 0)) : null;

      if (available === null) {
        // No stock record — add without allocation (will be validated at sync time)
        cartEntries.push(
          this.cartService.createCartEntry(
            product as any,
            { productCode: e.productCode, warehouseCode: e.warehouseCode ?? '', observation: e.observation ?? '' },
            entryIndex,
            e.qty,
          ),
        );
        entryIndex++;
        continue;
      }

      if (available === 0) {
        stockIssues.push({
          productCode: e.productCode,
          productName: e.productName ?? e.productCode,
          status: 'OUT_OF_STOCK',
          requestedQty: e.qty,
          availableQty: 0,
        });
        continue;
      }

      if (available < e.qty) {
        stockIssues.push({
          productCode: e.productCode,
          productName: e.productName ?? e.productCode,
          status: 'ADJUSTED',
          requestedQty: e.qty,
          availableQty: available,
        });
      }

      const finalQty = available < e.qty ? available : e.qty;
      cartEntries.push(
        this.cartService.createCartEntry(
          product as any,
          {
            productCode: e.productCode,
            warehouseCode: e.warehouseCode ?? '',
            observation: e.observation ?? '',
            stockAllocations: [{ type: StockAllocationType.IMMEDIATE, quantity: finalQty }],
          },
          entryIndex,
          finalQty,
        ),
      );
      entryIndex++;
    }

    cart.entries = cartEntries;

    let recalculated = await this.cartService.recalculateCart(cart, b2bUnitUid);

    await firstValueFrom(this.cartRepo.addOrUpdate(recalculated));

    if (order.coupon) {
      try {
        recalculated = await this.couponService.applyCoupon(`/cart/${recalculated.code}`, order.coupon, b2bUnitUid);
      } catch {
        // Coupon may be expired/invalid — proceed without it
      }
    }

    await firstValueFrom(this.cartRepo.addOrUpdate(recalculated));
    localStorage.setItem(DefaultLocalCartService.OFFLINE_CART_CODE_KEY, recalculated.code);

    return { cart: recalculated, stockIssues };
  }

  /**
   * Lista pedidos de referência elegíveis para a data de entrega desejada.
   */
  async listReferenceOrders(
    desiredDeliveryDate: string,
    unitUid: string,
    customerUid: string,
  ): Promise<OrderReferenceSelectItem[]> {
    const requestedDate = this.helper.parseDateOnly(desiredDeliveryDate);
    if (!requestedDate || !unitUid || !customerUid) return [];

    const orders = await firstValueFrom(this.orderReferenceRepo.getAll());

    return orders
      .filter(order => {
        if (!order.code) return false;
        if (
          String(order.status ?? '')
            .trim()
            .toUpperCase() === 'CANCELLED'
        )
          return false;
        if (String(order.unitUid ?? '').trim() !== unitUid) return false;
        if (String(order.customerUid ?? '').trim() !== customerUid) return false;

        const orderDate = this.helper.parseDateOnly(order.desiredDeliveryDate);
        return orderDate ? this.helper.isSameDate(orderDate, requestedDate) : false;
      })
      .sort((left, right) => this.getTimestamp(right) - this.getTimestamp(left))
      .map(order => ({ code: order.code, description: order.code }));
  }

  /**
   * Reconstrói um carrinho a partir de um OfflineOrderOutboxEntry.
   * Usado quando o pedido a ser copiado é um order_outbox (modo offline).
   */
  private async copyFromOutboxEntry(
    outboxEntry: OfflineOrderOutboxEntry,
    b2bUnitUid: string,
  ): Promise<CopyOrderToCartResult> {
    const { display, request } = outboxEntry;

    const cart = this.cartService.createEmptyCart();

    cart.warehouseCode = display.warehouseCode ?? undefined;
    cart.documentTypeCode = request.documentTypeCode ?? undefined;
    cart.freightTypeCode = request.freightTypeCode ?? undefined;
    cart.purchaseOrder = request.purchaseOrder ?? undefined;
    cart.observation = request.observation ?? undefined;
    cart.desiredDeliveryDate = request.desiredDeliveryDate ?? undefined;
    cart.paymentMethodCode = request.paymentMethodCode ?? undefined;
    cart.paymentConditionCode = request.paymentConditionCode ?? undefined;

    const allStockLevels = await firstValueFrom(this.stockLevelRepo.getAll());
    const stockIssues: StockIssue[] = [];
    const cartEntries: CartEntry[] = [];
    let entryIndex = 0;

    for (const e of display.entries) {
      const product = await firstValueFrom(this.productRepo.getByKey(e.productCode));
      const warehouseCode = e.warehouseCode ?? display.warehouseCode ?? '';

      const stockLevel = allStockLevels.find(s => s.productCode === e.productCode && s.warehouseCode === warehouseCode);
      const available =
        stockLevel != null ? Math.max(0, (stockLevel.available ?? 0) - (stockLevel.reserved ?? 0)) : null;

      if (available === null) {
        // No stock record — add without allocation (will be validated at sync time)
        cartEntries.push(
          this.cartService.createCartEntry(
            product as any,
            { productCode: e.productCode, warehouseCode, observation: e.observation ?? '' },
            entryIndex,
            e.qty,
          ),
        );
        entryIndex++;
        continue;
      }

      if (available === 0) {
        stockIssues.push({
          productCode: e.productCode,
          productName: e.productName,
          status: 'OUT_OF_STOCK',
          requestedQty: e.qty,
          availableQty: 0,
        });
        continue;
      }

      if (available < e.qty) {
        stockIssues.push({
          productCode: e.productCode,
          productName: e.productName,
          status: 'ADJUSTED',
          requestedQty: e.qty,
          availableQty: available,
        });
      }

      const finalQty = available < e.qty ? available : e.qty;
      cartEntries.push(
        this.cartService.createCartEntry(
          product as any,
          {
            productCode: e.productCode,
            warehouseCode,
            observation: e.observation ?? '',
            stockAllocations: [{ type: StockAllocationType.IMMEDIATE, quantity: finalQty }],
          },
          entryIndex,
          finalQty,
        ),
      );
      entryIndex++;
    }

    cart.entries = cartEntries;

    let recalculated = await this.cartService.recalculateCart(cart, b2bUnitUid);
    await firstValueFrom(this.cartRepo.addOrUpdate(recalculated));

    if (request.coupon) {
      try {
        recalculated = await this.couponService.applyCoupon(`/cart/${recalculated.code}`, request.coupon, b2bUnitUid);
      } catch {
        // Cupom pode estar expirado/inválido — prosseguir sem ele
      }
    }

    await firstValueFrom(this.cartRepo.addOrUpdate(recalculated));
    localStorage.setItem(DefaultLocalCartService.OFFLINE_CART_CODE_KEY, recalculated.code);

    return { cart: recalculated, stockIssues };
  }

  private async resolvePaymentCode(
    storedCode: string | null | undefined,
    description: string | null | undefined,
    repo: PaymentMethodRepository | PaymentConditionRepository,
  ): Promise<string | undefined> {
    if (storedCode) return storedCode;
    if (!description) return undefined;
    try {
      const all = await firstValueFrom(repo.getAll());
      return all.find(item => item.description === description)?.code ?? undefined;
    } catch {
      return undefined;
    }
  }

  private getTimestamp(order: { createdAt?: string | null }): number {
    const createdAt = String(order.createdAt ?? '').trim();
    const ts = createdAt ? new Date(createdAt).getTime() : 0;
    return Number.isNaN(ts) ? 0 : ts;
  }
}
